import React, { ElementType, ReactNode } from 'react';
import { AvStepper, AvStepperProps } from '@fabric-ui/components';
import { X } from '@fabric-ui/icons';
import { FailFull as Error, WarningFullOrange as Warn } from '@fabric-ui/icons/colorful';
import { flex } from '@fabric-ui/theme';
import { Box, Dialog, DialogContent, DialogProps, DialogTitle, Divider, IconButton, PaperProps } from '@mui/material';

import IntrinsicElements = React.JSX.IntrinsicElements;

const titleIconVariants = {
  error: <Error style={{ width: 24, height: 24 }} />,
  warning: <Warn style={{ width: 24, height: 24 }} />,
};

export const SIZES = {
  sm: 350,
  md: 550,
  lg: 800,
  xl: 1300,
};

export interface AvDialogProps {
  title: ReactNode;
  titleVariant?: 'error' | 'warning' | 'compact';
  footer?: ReactNode;
  footerStepperProps?: AvStepperProps;
  size?: DialogProps['maxWidth'];
  open: boolean;
  onClose: () => void;
  children?: ReactNode;
  noPadding?: boolean;
  fullWidth?: boolean;
  isLoading?: boolean;
  PaperProps?: Partial<PaperProps<ElementType<any, keyof IntrinsicElements>>>;
  header?: ReactNode;
}
export const AvDialog: React.FC<AvDialogProps> = ({
  title,
  titleVariant,
  footer,
  footerStepperProps,
  size = 'md',
  open,
  onClose,
  children,
  noPadding = false,
  fullWidth,
  isLoading = false,
  PaperProps,
  header,
}) => (
  <Dialog fullWidth={fullWidth} open={open} onClose={onClose} maxWidth={size} PaperProps={PaperProps}>
    <div>
      {header && <div>{header}</div>}
      <Box
        sx={{
          ...(titleVariant === 'compact'
            ? { ...flex.justifyBetweenCenter, paddingTop: 0, backgroundColor: theme => theme.palette.white.main }
            : { ...flex.justifyBetweenStart, paddingTop: 3 }),
          pr: 3,
        }}>
        <DialogTitle
          sx={{
            ...flex.itemsCenter,
            gap: 1,
            pt: 1,
            pl: titleVariant ? 2.75 : size === 'xl' ? 4 : 3,
            ...(titleVariant === 'compact' && { py: 1, px: 2 }),
          }}>
          {titleVariant ? titleIconVariants[titleVariant] : undefined}
          {title}
        </DialogTitle>
        <IconButton onClick={onClose}>
          <X style={{ width: 20, height: 20 }} />
        </IconButton>
      </Box>
    </div>
    <DialogContent
      sx={{
        px: noPadding ? 0 : size === 'sm' ? 3 : 4,
        width: '100%',
        minWidth: size && SIZES[size],
        maxWidth: size && SIZES[size],
        minHeight: 'auto',
        ...(PaperProps && { height: '100%' }),
      }}>
      <Box noValidate component="form" sx={{ ...(PaperProps && { height: '100%' }) }}>
        {titleVariant === 'compact' && <Divider />}
        {children}
      </Box>
    </DialogContent>
    {(footer || footerStepperProps) && (
      <Box sx={{ ...flex.col, gap: footerStepperProps ? 0 : 2 }}>
        {isLoading || footerStepperProps ? (
          <AvStepper isLoading={isLoading} sx={{ position: 'absolute', width: '100%', zIndex: 1, padding: 0 }} {...footerStepperProps} />
        ) : (
          <Divider sx={{ height: '4px' }} />
        )}
        {footer && <Box sx={{ pb: 2, px: 3 }}>{footer}</Box>}
      </Box>
    )}
  </Dialog>
);
